import { z } from 'zod/v4';

/**
 * Path parameters of `POST /api/questionnaires/requests/:requestId/certificates`.
 */
export const CreateQuestionnaireRequestCertificateParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
});

export type CreateQuestionnaireRequestCertificateParams = z.infer<
  typeof CreateQuestionnaireRequestCertificateParamsSchema
>;

/**
 * ISO 8601 datetime, matching the `/api/claims` contract's own validator
 * character for character rather than using `z.iso.datetime()`.
 *
 * Deliberate: the two endpoints write the same columns, and the date handling
 * here has already been the subject of one regression — the migrated claims
 * schema was stricter than the legacy Ajv one and started rejecting updates the
 * certificate UI had always sent (DELTA-2342). Diverging again, even towards a
 * "better" validator, would risk the same class of break.
 */
const isoDateTimeString = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/,
    'Must be a valid ISO 8601 datetime string (e.g., 2026-02-25T17:00:00.000Z)',
  );

/**
 * Body of `POST /api/questionnaires/requests/:requestId/certificates`.
 *
 * **No `companyId`** — the certificate is created on the company the answer
 * request is about, derived from the request. This is the whole reason the route
 * exists: `POST /v3/claims` takes the company in its body, inserts the row, and
 * then reads it back through a network-scoped query, so a delegated answering
 * party got a 404 *after* the claim and its private-bucket file copy had already
 * been written — an orphan the reads cannot see and the duplicate check cannot
 * find, so every retry made another one.
 *
 * Required and optional fields mirror `POST /api/claims` exactly — only the two
 * dates and the standard are required. The legacy answer modal happens to require
 * all seven of its fields, but the v2 certificate dialog requires only the dates,
 * so a stricter contract here would reject submissions the generic endpoint
 * accepts. Nothing about delegation justifies a different field contract.
 *
 * What is *dropped* relative to the generic endpoint: `styleIds`,
 * `shouldAttachChildren`, `renewedId` and `status`. The first three are product
 * and renewal concerns the answer flow does not offer — and are omitted rather
 * than ignored, so nothing a caller sends is silently discarded. `status` is
 * excluded on purpose: a certificate filed on another company's profile must not
 * be client-settable to APPROVED.
 */
export const CreateQuestionnaireRequestCertificateBodySchema = z.object({
  standardId: z.string().min(1).max(255),
  issueDate: isoDateTimeString,
  expirationDate: isoDateTimeString,
  fileId: z.string().min(1).max(36).nullable().optional(),
  issuedBy: z.string().max(255).nullable().optional(),
  documentNumber: z.string().min(1).max(255).nullable().optional(),
  licenseNumber: z.string().min(1).max(255).nullable().optional(),
  explanation: z.string().nullable().optional(),
});

export type CreateQuestionnaireRequestCertificateBody = z.infer<
  typeof CreateQuestionnaireRequestCertificateBodySchema
>;

/**
 * The created certificate, as the answer page needs it: enough to link it as the
 * answer. The page refetches the picker list for display, so the full claim
 * projection would be work nobody reads.
 */
export const CreateQuestionnaireRequestCertificateResponseSchema = z.object({
  id: z.string(),
  standardId: z.string().nullable(),
  companyId: z.string().nullable(),
});

export type CreateQuestionnaireRequestCertificateResponse = z.infer<
  typeof CreateQuestionnaireRequestCertificateResponseSchema
>;
